/**
 * Accordion Component
 * --------------------------------------------------------------------
 * @summary accordion component, hides/shows elements based on header button click, auto-collapses sibling accordions by default
 *
 * @usage
 *   <s-accordion data-manual-collapse="false" js-accordion>
 *      <div class="accordion-item" js-accordion-item>
 *        <button class="accordion-header" aria-expanded="true" aria-controls="accordionID" js-accordion-header>Description</button>
 *        <div id="accordionID" class="accordion-content" aria-hidden="false" js-accordion-content>
 *          ...
 *        </div>
 *      </div>
 *    </s-accordion>
 *
 * The following parameters are attributes that the <s-accordion> element takes
 * @param data-manual-collapse - set attribute to "true" to disable auto-collapse siblings
 * @param data-manual-children - set attribute to "false" to disable auto-collapsing children accordions
 *
 * The following are selectors that the <s-accordion> searches for
 * [js-accordion] - required
 * [js-accordion-item] - required
 * [js-accordion-header] - required
 * [js-accordion-content] - required
 * [data-manual-collapse] - optional
 *
 * .accordion-content[aria-hidden="false"] - this styling can be adjusted based on content + desired animation
 */

class Accordion extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      accordion: '[js-accordion]',
      accordionItem: '[js-accordion-item]',
      accordionHeader: '[js-accordion-header]',
      accordionContent: '[js-accordion-content]',
    };
  }

  connectedCallback() {
    this.buttons = this.querySelectorAll(this._selectors.accordionHeader);
    this.buttons.forEach((button) => {
      button.addEventListener('click', this._toggleContent);
    });

    this.content = this.querySelectorAll(this._selectors.accordionContent);
  }

  _toggleContent = (e) => {
    e.stopImmediatePropagation();
    e.preventDefault();
    const trigger = e.currentTarget;
    const content = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
    const siblings = this._getSiblings(trigger.parentElement, this._selectors.accordionItem);
    let isExpanded = trigger.getAttribute('aria-expanded');
    if (isExpanded == 'false') {
      trigger.setAttribute('aria-expanded', true);
      this._prepareTransition(content);
      this._expandSection(content);

      if (!this.dataset.manualCollapse) {
        siblings.forEach((sibling) => {
          const siblingTrigger = sibling.querySelector(this._selectors.accordionHeader);
          const siblingContent = sibling.querySelector(`#${siblingTrigger.getAttribute('aria-controls')}`);
          if (siblingTrigger.getAttribute('aria-expanded') == 'true') {
            siblingTrigger.setAttribute('aria-expanded', false);
            this._collapseSection(siblingContent);
          }
        });
      }
    } else {
      trigger.setAttribute('aria-expanded', false);
      this._collapseSection(content);
    }
  };

  _collapseSection(element) {
    // mark the section as "currently hidden"
    element.setAttribute('aria-hidden', true);
  }

  _expandSection(element) {
    // mark the section as "currently not hidden"
    element.setAttribute('aria-hidden', false);
  }

  _getSiblings(element, selector) {
    let siblings = [];
    let targets;

    if (selector) {
      targets = element.parentNode.querySelectorAll(':scope > ' + selector);
    } else {
      targets = element.parentNode.children;
    }

    for (let target of targets) {
      if (target !== element) siblings.push(target);
    }

    return siblings;
  }

  _prepareTransition(element) {
    element.addEventListener('transitionend', () => {
      element.classList.remove('is-transitioning');
    });

    // check the various CSS properties to see if a duration has been set
    var cl = [
      'transition-duration',
      '-moz-transition-duration',
      '-webkit-transition-duration',
      '-o-transition-duration',
    ];
    let duration = 0;
    const computedStyles = getComputedStyle(element);
    cl.forEach((prop) => duration || (duration = parseFloat(computedStyles.getPropertyValue(prop))));

    // if I have a duration then add the class
    if (duration != 0) {
      element.classList.add('is-transitioning');
      element.offsetWidth; // check offsetWidth to force the style rendering
    }
  }
}
customElements.define('s-accordion', Accordion);

/**
 * <s-tabs>
 *   <div class="tab__triggers" role="tablist" aria-orientation="horizontal" js-tablist>
 *     <button id="tab1-tab" class="tab__trigger" aria-controls="tab1-content-panel" role="tab" aria-selected="true" js-tab>Tab1</button>
 *     <button id="tab2-tab" class="tab__trigger" aria-controls="tab2-content-panel" role="tab" aria-selected="false" js-tab>Tab2</button>
 *     <button id="tab3-tab" class="tab__trigger" aria-controls="tab3-content-panel" role="tab" aria-selected="false" js-tab>Tab3</button>
 *   </div>
 *   <div id="tab1-content-panel" class="tab__panel" role="tabpanel" aria-labelledby="tab1-tab" aria-hidden="false"></div>
 *   <div id="tab2-content-panel" class="tab__panel" role="tabpanel" aria-labelledby="tab2-tab" aria-hidden="true"></div>
 *   <div id="tab3-content-panel" class="tab__panel" role="tabpanel" aria-labelledby="tab3-tab" aria-hidden="true"></div>
 * </s-tabs>
 **/

class Tabs extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this._elements = {
      tabs: this.querySelectorAll('[js-tab]'),
      tabList: this.querySelectorAll('[js-tab-list]'),
      tabPanel: this.querySelectorAll('[js-tab-panel]'),
    };

    this._tabEventListener();
    this._tabListListener();
  }

  _tabEventListener = () => {
    const { tabs } = this._elements;
    tabs.forEach((tab) => tab.addEventListener('click', this._showTabPanel));
  };

  _showTabPanel = (el) => {
    const { tabs } = this._elements;
    for (let i = 0; i < tabs.length; i++) {
      tabs[i].setAttribute('aria-selected', 'false');
    }
    el.target.setAttribute('aria-selected', 'true');
    var tabPanelToOpen = el.target.getAttribute('aria-controls');
    var tabPanels = document.querySelectorAll('[role=tabpanel]');
    for (let i = 0; i < tabPanels.length; i++) {
      tabPanels[i].setAttribute('aria-hidden', 'true');
    }
    document.getElementById(tabPanelToOpen).setAttribute('aria-hidden', 'false');
  };

  _tabListListener = () => {
    const { tabList } = this._elements;
    tabList.forEach((list) => list.addEventListener('keydown', this._tabListKeydown));
  };

  _tabListKeydown = (e) => {
    if (e.keyCode == 37) {
      $('[aria-selected=true]').prev().click().focus();
      e.preventDefault();
    }
    if (e.keyCode == 38) {
      $('[aria-selected=true]').prev().click().focus();
      e.preventDefault();
    }
    if (e.keyCode == 39) {
      $('[aria-selected=true]').next().click().focus();
      e.preventDefault();
    }
    if (e.keyCode == 40) {
      $('[aria-selected=true]').next().click().focus();
      e.preventDefault();
    }
  };
}
customElements.define('s-tabs', Tabs);
